import { useEffect, useRef, useState, type CSSProperties } from "react";
import { sound } from "../../audio/sound";
import { PRIVACY_MATERIAL_VERSION, privacyLinkVisible } from "../../legal/privacy";
import { loadQuickPlay, saveQuickPlay, type StoredQuickPlay } from "../../state/persistence";
import { useVoiceStore } from "../../state/voiceStore";
import { TableConfigPanel } from "./TableConfigPanel";
import {
  autoJoinEnabled,
  getVoiceLevel,
  isAgeGated,
  loadPrivacyAck,
  setAutoJoin,
  setVoiceLevel,
  voiceReadyBand,
} from "../../voice/consent";

/**
 * Settings, opened from the cog beside Table & Cards on the home screen.
 *
 * Sound, voice chat, and the table Quick Play deals. Sound used to sit at the foot
 * of Table & Cards, on the argument that the ambience is a property of the venue - but that page
 * is a showroom, and the two switches a player actually goes looking for were
 * below every shelf on it. A sheet off the home screen is one tap from anywhere
 * a player starts, and has room for whatever setting comes next.
 *
 * It is also the second door to the privacy policy, after the link beside the
 * version badge: Settings is where people look for it. `onOpenPrivacy` is the
 * screen's to handle, because the policy sheet cannot open over this one.
 */
export function SettingsModal({ onClose, onOpenPrivacy }: { onClose: () => void; onOpenPrivacy?: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [musicOn, setMusicOn] = useState(sound.isMusicOn());
  const [sfxOn, setSfxOn] = useState(sound.isSfxOn());
  const [musicVolume, setMusicVolume] = useState(sound.getMusicVolume());
  // Voice is offered where the policy is, and never to a device that answered
  // the age question under 13.
  const voiceShown = privacyLinkVisible() && !isAgeGated();
  const [voiceBand] = useState(() => voiceReadyBand(loadPrivacyAck(), PRIVACY_MATERIAL_VERSION));
  const [voiceLevel, setVoiceLevelState] = useState(getVoiceLevel());
  const [autoJoin, setAutoJoinState] = useState(() => autoJoinEnabled(voiceBand));
  // The Quick Play table, saved on every change like the setup screen's. Folded
  // by default: it is the tallest thing here and set once, not every visit.
  const [quickPlay, setQuickPlay] = useState<StoredQuickPlay>(loadQuickPlay);
  const [quickPlayOpen, setQuickPlayOpen] = useState(false);
  function changeQuickPlay(next: StoredQuickPlay) {
    setQuickPlay(next);
    saveQuickPlay(next);
  }

  // Escape closes and the panel takes focus on open, like the other sheets.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="confirm-overlay" onClick={onClose}>
      <div
        ref={panelRef}
        className="card-panel col confirm-panel settings-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="settings-title" className="title confirm-title">
          Settings
        </h2>
        {/* The title and Done stay put and everything between them scrolls.
            The panel itself must not be the scroller: its stitching is drawn
            by an absolute ::before sized to the panel's box, which stays one
            screen tall and scrolls off with the content once Quick Play is
            open. */}
        <div className="col settings-scroll">
        <div className="section-label">Sound</div>
        {/* Each switch sits over what it governs: Ambience over its volume,
            Effects on its own. */}
        <label className="check-row">
          <input
            type="checkbox"
            checked={musicOn}
            onChange={(e) => {
              sound.setMusicOn(e.target.checked);
              setMusicOn(e.target.checked);
            }}
          />
          Ambience
        </label>
        {/* Disabled rather than hidden while the ambience is off: the level set is waiting for them. */}
        <div className="slider-field">
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(musicVolume * 100)}
            disabled={!musicOn}
            aria-label="Ambience volume"
            style={{ "--slider-fill": musicVolume } as CSSProperties}
            onChange={(e) => {
              const next = Number(e.target.value) / 100;
              sound.setMusicVolume(next);
              setMusicVolume(next);
            }}
          />
          <div className="subtitle">{musicOn ? `Ambience volume ${Math.round(musicVolume * 100)}%` : "Ambience is off"}</div>
        </div>
        <label className="check-row">
          <input
            type="checkbox"
            checked={sfxOn}
            onChange={(e) => {
              sound.setSfxOn(e.target.checked);
              setSfxOn(e.target.checked);
            }}
          />
          Effects
        </label>
        {voiceShown && (
          <>
            <div className="section-label">Voice</div>
            {/* Not offered at 13-17: arriving never joins voice for them, so
                the switch would do nothing (see voiceStore's shouldAutoJoin). */}
            {voiceBand !== "13-17" && (
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={autoJoin}
                  onChange={(e) => {
                    setAutoJoin(e.target.checked);
                    setAutoJoinState(e.target.checked);
                  }}
                />
                Join voice automatically
              </label>
            )}
            {/* Its own level so a player can keep the voices with the music
                off; the HUD's speaker button still silences everything. */}
            <div className="slider-field">
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(voiceLevel * 100)}
                aria-label="Voice volume"
                style={{ "--slider-fill": voiceLevel } as CSSProperties}
                onChange={(e) => {
                  const next = Number(e.target.value) / 100;
                  setVoiceLevel(next);
                  setVoiceLevelState(next);
                  useVoiceStore.getState().refreshOutput();
                }}
              />
              <div className="subtitle">Voice volume {Math.round(voiceLevel * 100)}%</div>
            </div>
          </>
        )}
        {/* Every table setting the setup screen has, for a table of one: Quick
            Play is always solo, so the panel is told so and drops what a solo
            table cannot use (Blue Shell). */}
        <button
          type="button"
          className={`section-label rules-toggle rules-fold${quickPlayOpen ? " is-open" : ""}`}
          aria-expanded={quickPlayOpen}
          aria-controls="settings-quick-play"
          onClick={() => setQuickPlayOpen((o) => !o)}
        >
          Quick Play
          <span className="rules-chevron" aria-hidden="true" />
        </button>
        {quickPlayOpen && (
          <div id="settings-quick-play" className="col">
            <div className="subtitle subtle">The table Quick Play deals you, on your own.</div>
            <TableConfigPanel
              playerCount={1}
              deckConfig={quickPlay.deckConfig}
              houseRules={quickPlay.houseRules}
              turnTimerSeconds={quickPlay.turnTimerSeconds}
              onChange={changeQuickPlay}
            />
          </div>
        )}
        {onOpenPrivacy && privacyLinkVisible() && (
          <>
            <div className="section-label">About</div>
            <button className="settings-link" onClick={onOpenPrivacy}>
              Privacy policy
            </button>
          </>
        )}
        </div>
        <div className="row confirm-actions">
          {/* Plain, not brass: the switches above are what this sheet is for. */}
          <button onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
